import styles from './Attachments.module.scss';

import clsx from 'clsx';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Accept,
  DropzoneOptions,
  FileRejection,
  useDropzone,
} from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { TempFileManagerContext } from '@work4all/data/lib/hooks/data-provider/useTempFileManager';
import { useCustomSnackbar } from '@work4all/data/lib/snackbar/PresetSnackbarProvider';

import { useLock } from '../../hooks';

import { AttachmentProps } from './Attachments';

export interface AttachmentsDropZoneType {
  reset: () => void;
}

export const AttachmentsDropZone = forwardRef(function AttachmentsDropZone(
  props: Omit<AttachmentProps, 'entity'> & {
    children;
    className?: string;
    single?: boolean;
    maxSize?: number;
    accept?: Accept;
    onDropRejected?: DropzoneOptions['onDropRejected'];
  },
  ref: ForwardedRef<AttachmentsDropZoneType>
) {
  const {
    children,
    disableAddAction = false,
    fullHeight = true,
    className = 'string',
    single = false,
    maxSize,
    accept,
    onDropRejected,
  } = props;

  const { t } = useTranslation();

  const lock = useLock();

  const [bringToFront, setBringToFront] = useState(true);

  // Manually handle active state as the dropzone is sometimes used,
  // by desing, with Froala editor inside. Froala handles 'drop'
  // on its own, so the dropzone remains active, after drop in Editor
  // - drop state is not caught by drop handlers from react-dropzone
  const [fileDragActive, setFileDragActive] = useState(false);

  const { enqueueSnackbar, hideApiErrors } = useCustomSnackbar();
  const errorSnackbar = useCallback(
    (message: string, timeout: number) => {
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: timeout,
      });
    },
    [enqueueSnackbar]
  );

  const attachmentsContext = useContext(TempFileManagerContext);
  const { uploadFiles: uploadAttachments } = attachmentsContext;
  const isUploadActionAllowed =
    !disableAddAction &&
    !lock?.locked &&
    !(single && attachmentsContext.fileList.length);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (isUploadActionAllowed) {
        const rejectionError = fileRejections[0]?.errors[0].code;

        if (rejectionError) {
          switch (rejectionError) {
            case 'file-too-large':
              if (maxSize) {
                errorSnackbar(
                  t('MASK.ATTACHMENT_CUSTOM_MAX_SIZE', {
                    size: maxSize / 1024 / 1024,
                  }),
                  3000
                );
              }
              break;
            default:
              if (!hideApiErrors)
                errorSnackbar(t('ERROR.COULD_NOT_REACH_SERVICE'), 3000);
              break;
          }
        } else {
          uploadAttachments(acceptedFiles);
        }
      }
      setFileDragActive(false);
    },
    [
      errorSnackbar,
      hideApiErrors,
      isUploadActionAllowed,
      maxSize,
      t,
      uploadAttachments,
    ]
  );

  const onDragEnter = useCallback(
    (e) => {
      if (isUploadActionAllowed) {
        setFileDragActive(true);
        if (e.dataTransfer.items[0]?.type.startsWith('image/')) {
          setBringToFront(false);
        }
      }
    },
    [isUploadActionAllowed]
  );

  const onDragLeave = useCallback(() => {
    setBringToFront(false);
    setFileDragActive(false);
  }, []);

  const { getRootProps } = useDropzone({
    onDrop,
    onDropRejected,
    onDragEnter,
    onDragLeave,
    accept,
    maxSize,
  });

  useImperativeHandle(
    ref,
    () => {
      return {
        reset: onDragLeave,
      };
    },
    [onDragLeave]
  );
  const rootProps = getRootProps();
  return (
    <div
      {...rootProps}
      onMouseLeave={() => {
        onDragLeave();
      }}
      className={clsx(className, styles.dropArea, {
        [styles.dropzoneActive]: fileDragActive,
        [styles.fullHeight]: fullHeight,
        [styles.toFront]: bringToFront,
      })}
    >
      {children}
    </div>
  );
});
